import { useEffect, useLayoutEffect, useMemo, useState } from 'react'

import { Footer } from '@/components/footer'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  blogListPath,
  blogSources,
  resolveBlogSource,
  type BlogMetadata,
  type ResolvedBlogSource,
} from '@/content/blogs'
import { useI18n } from '@/lib/i18n'
import { VALID_LANGUAGES, type LanguageCode } from '@/lib/url-language-utils'
import { formatDate } from '@/lib/utils'
import { applyContentPageHead, contentLanguageTag } from '@/pages/content-link-utils'

interface BlogListEntry extends BlogMetadata, ResolvedBlogSource {}

type BlogListLoadState
  = | { language: LanguageCode, status: 'ready', blogs: BlogListEntry[] }
    | { language: LanguageCode, status: 'error' }

const EMPTY_BLOGS: BlogListEntry[] = []

export function BlogListPage() {
  const { language, t, isLoading: translationsLoading } = useI18n()
  const [hasServerPrelude] = useState(() =>
    typeof document !== 'undefined'
    && document.getElementById('seo-blog-list-prelude') !== null)
  const [loadState, setLoadState] = useState<BlogListLoadState | null>(null)

  const resolvedSources = useMemo(() => blogSources
    .map(source => resolveBlogSource(source, language))
    .filter((source): source is ResolvedBlogSource => source !== null), [language])

  useEffect(() => {
    let isMounted = true

    const loadMetadata = async () => {
      try {
        if (resolvedSources.length !== blogSources.length)
          throw new Error('A published blog source has no readable variant')

        const blogs = await Promise.all(
          resolvedSources.map(async (source) => {
            const response = await fetch(`${source.basePath}/metadata.json`)
            if (!response.ok)
              throw new Error(`Failed to load metadata for ${source.id}`)

            const metadata = (await response.json()) as BlogMetadata
            if (!metadata
              || typeof metadata.title !== 'string'
              || typeof metadata.description !== 'string'
              || typeof metadata.publishedAt !== 'string'
              || typeof metadata.coverImage !== 'string'
              || !metadata.title.trim()
              || !metadata.description.trim()
              || !metadata.publishedAt.trim()) {
              throw new Error(`Invalid metadata for ${source.id}`)
            }
            return { ...metadata, ...source }
          }),
        )
        if (!isMounted)
          return

        blogs.sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime())
        setLoadState({ language, status: 'ready', blogs })
      }
      catch (error) {
        console.error(error)
        if (isMounted)
          setLoadState({ language, status: 'error' })
      }
    }

    void loadMetadata()
    return () => {
      isMounted = false
    }
  }, [language, resolvedSources])

  const currentList = loadState?.language === language ? loadState : null
  const isLoading = currentList === null
  const hasLoadFailed = currentList?.status === 'error'
  const blogs = currentList?.status === 'ready' ? currentList.blogs : EMPTY_BLOGS
  const hasNoContent = currentList?.status === 'ready' && blogs.length === 0

  useLayoutEffect(() => {
    if (translationsLoading || isLoading)
      return

    if (blogs.length === 0 && document.getElementById('seo-blog-list-prelude'))
      return

    const title = `${t('blog.list.title')} | Mentiss AI`
    const description = t('blog.list.description')
    const canonicalUrl = `https://mentiss.ai${blogListPath(language)}`
    const alternates = VALID_LANGUAGES.map(code => ({
      hreflang: contentLanguageTag(code),
      href: `https://mentiss.ai${blogListPath(code)}`,
    }))
    alternates.push({
      hreflang: 'x-default',
      href: `https://mentiss.ai${blogListPath('en')}`,
    })

    const cleanup = applyContentPageHead({
      title,
      description,
      htmlLanguage: contentLanguageTag(language),
      interfaceLanguage: language,
      robots: blogs.length > 0 ? 'index,follow' : 'noindex,nofollow',
      canonicalUrl: blogs.length > 0 ? canonicalUrl : undefined,
      alternates: blogs.length > 0 ? alternates : [],
      structuredDataId: blogs.length > 0 ? 'blog-list-structured-data' : undefined,
      structuredData: blogs.length > 0 ? {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        'name': t('blog.list.title'),
        description,
        'url': canonicalUrl,
        'inLanguage': contentLanguageTag(language),
        'mainEntity': {
          '@type': 'ItemList',
          'itemListElement': blogs.map((blog, index) => ({
            '@type': 'ListItem',
            'position': index + 1,
            'name': blog.title,
            'url': `https://mentiss.ai${blog.path}`,
          })),
        },
      } : undefined,
    })
    if (blogs.length > 0)
      document.getElementById('seo-blog-list-prelude')?.remove()
    return cleanup
  }, [blogs, isLoading, language, t, translationsLoading])

  if (hasServerPrelude && (isLoading || hasLoadFailed || hasNoContent || translationsLoading))
    return null

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border bg-background/95 pt-24 backdrop-blur">
        <div className="mx-auto flex max-w-5xl flex-col gap-2 px-4 pb-10">
          <p className="text-sm text-muted-foreground">{t('blog.list.tagline')}</p>
          <h1 className="text-3xl font-bold md:text-4xl">
            {t('blog.list.title')}
          </h1>
          <p className="text-base text-muted-foreground md:text-lg">
            {t('blog.list.description')}
          </p>
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 py-10">
        {isLoading ? (
          <p className="text-muted-foreground">{t('blog.list.loading')}</p>
        ) : hasLoadFailed ? (
          <p role="alert" className="text-destructive">{t('blog.article.loadFailedMessage')}</p>
        ) : hasNoContent ? (
          <p className="text-muted-foreground">{t('blog.list.noArticles')}</p>
        ) : (
          <div className="grid gap-6 md:grid-cols-2">
            {blogs.map(blog => (
              <Card key={blog.id} className="overflow-hidden transition hover:shadow-lg">
                <a href={blog.path} className="flex h-full flex-col">
                  <img
                    src={blog.coverImage}
                    alt={blog.title}
                    className="h-48 w-full object-cover"
                    loading="lazy"
                  />

                  <CardHeader className="flex-1">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">
                      {formatDate(blog.publishedAt)}
                    </p>
                    <CardTitle className="line-clamp-2 text-xl" lang={contentLanguageTag(blog.locale)}>{blog.title}</CardTitle>
                    <CardDescription className="line-clamp-3 text-base" lang={contentLanguageTag(blog.locale)}>
                      {blog.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="pt-0">
                    <span className="text-sm font-medium text-primary">Read article →</span>
                  </CardContent>
                </a>
              </Card>
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  )
}
